import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import AdoptPage from '@/pages/AdoptPage';
import RescuedAnimalsPage from '@/pages/RescuedAnimalsPage';
import RescuedAnimalDetailPage from '@/pages/RescuedAnimalDetailPage';
import NotFound from '@/pages/NotFound';
import { sheltersApi } from '@/features/shelters/api';

type ParsedRoute =
  | { kind: 'home'; slug: string }
  | { kind: 'rescued-list'; slug: string }
  | { kind: 'rescued-detail'; slug: string; id: string }
  | null;

const SLUG_SEGMENT = /^[^/.]+$/;

function parseRoute(pathname: string): ParsedRoute {
  const raw = pathname.replace(/^\/+|\/+$/g, '');
  if (!raw) return null;
  const parts = raw.split('/');

  if (parts.length === 1) {
    const [slug] = parts;
    if (!SLUG_SEGMENT.test(slug)) return null;
    return { kind: 'home', slug };
  }

  if (parts.length === 2 && parts[1] === 'encontrados') {
    const [slug] = parts;
    if (!SLUG_SEGMENT.test(slug)) return null;
    return { kind: 'rescued-list', slug };
  }

  if (parts.length === 3 && parts[1] === 'encontrados') {
    const [slug, , id] = parts;
    if (!SLUG_SEGMENT.test(slug) || !SLUG_SEGMENT.test(id)) return null;
    return { kind: 'rescued-detail', slug, id };
  }

  return null;
}

export default function SlugRouteResolver() {
  const location = useLocation();
  const route = useMemo(() => parseRoute(location.pathname), [location.pathname]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['shelter-slug', route?.slug],
    queryFn: () => sheltersApi.getBySlug(String(route?.slug)),
    enabled: !!route?.slug,
    retry: false,
  });

  if (!route) return <NotFound />;
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isError || !data) return <NotFound />;

  const shelter = data as any;

  if (route.kind === 'rescued-list') {
    return <RescuedAnimalsPage forcedShelter={shelter} />;
  }
  if (route.kind === 'rescued-detail') {
    return <RescuedAnimalDetailPage forcedShelter={shelter} idOverride={route.id} />;
  }
  return <AdoptPage forcedShelter={shelter} />;
}
